import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Message,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "@arco-design/web-react";
import { Plus, Settings2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import { PermissionGate } from "../../../lib/auth";

import { AUTHZ_MANAGEMENT_PERMISSIONS, AUTHZ_ROLE_DEFAULT_PAGE_SIZE } from "../config";
import { useAuthzRoleListRuntime } from "../runtime";
import { getAuthzRoleCreateErrorMessage, validateAuthzRoleCreateInput } from "../service";
import type { AuthzRoleCreateMutation, AuthzRoleItem, AuthzRoleStatus } from "../types";

const initialCreatePayload: AuthzRoleCreateMutation = { code: "", name: "", description: "" };

/** 通用权限角色列表页。 */
export function AuthzRoleListPage() {
  const navigate = useNavigate();
  const currentUserQuery = useCurrentAuthUser();
  const [keywordInput, setKeywordInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState<AuthzRoleStatus | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(AUTHZ_ROLE_DEFAULT_PAGE_SIZE);
  const [createVisible, setCreateVisible] = useState(false);
  const [createPayload, setCreatePayload] = useState<AuthzRoleCreateMutation>(initialCreatePayload);
  const { rolesQuery, createMutation, statusMutation } = useAuthzRoleListRuntime({
    keyword,
    status,
    page,
    pageSize,
  });

  const submitCreate = async () => {
    const validationMessage = validateAuthzRoleCreateInput(createPayload);
    if (validationMessage) {
      Message.error(validationMessage);
      return;
    }
    try {
      const role = await createMutation.mutateAsync(createPayload);
      Message.success("角色创建成功");
      setCreateVisible(false);
      setCreatePayload(initialCreatePayload);
      navigate(`/settings/org-workflow/roles/${role.id}`);
    } catch (error) {
      Message.error(getAuthzRoleCreateErrorMessage(error));
    }
  };

  const columns = [
    {
      title: "角色",
      dataIndex: "name",
      render: (_: unknown, role: AuthzRoleItem) => (
        <button
          type="button"
          style={{ border: 0, background: "transparent", color: "#165dff", cursor: "pointer", padding: 0 }}
          onClick={() => navigate(`/settings/org-workflow/roles/${role.id}`)}
        >
          <strong>{role.name}</strong>
          <div style={{ color: "#86909c", fontWeight: 400 }}>{role.code}</div>
        </button>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      render: (_: unknown, role: AuthzRoleItem) => (
        <Tag color={role.status === "enabled" ? "green" : "gray"}>
          {role.status === "enabled" ? "已启用" : "已停用"}
        </Tag>
      ),
    },
    { title: "成员", dataIndex: "memberCount", render: (value: number) => `${value} 人` },
    { title: "页面权限", dataIndex: "pagePermissionCount", render: (value: number) => `${value} 项` },
    { title: "操作权限", dataIndex: "actionPermissionCount", render: (value: number) => `${value} 项` },
    { title: "更新时间", dataIndex: "updatedAt" },
    {
      title: "操作",
      render: (_: unknown, role: AuthzRoleItem) => (
        <Space>
          <Button size="small" icon={<Settings2 size={14} />} onClick={() => navigate(`/settings/org-workflow/roles/${role.id}`)}>
            配置
          </Button>
          <PermissionGate user={currentUserQuery.data} permission={AUTHZ_MANAGEMENT_PERMISSIONS.update}>
          <Popconfirm
            title={`确认${role.status === "enabled" ? "停用" : "启用"}角色“${role.name}”？`}
            onOk={async () => {
              await statusMutation.mutateAsync({
                roleId: role.id,
                status: role.status === "enabled" ? "disabled" : "enabled",
              });
              Message.success("角色状态已更新");
            }}
          >
            <Button size="small" status={role.status === "enabled" ? "danger" : "success"}>
              {role.status === "enabled" ? "停用" : "启用"}
            </Button>
          </Popconfirm>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card>
        <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap" }}>
          <Space wrap>
            <Input.Search
              allowClear
              aria-label="搜索角色"
              placeholder="角色名称或编码"
              style={{ width: 280 }}
              value={keywordInput}
              onChange={setKeywordInput}
              onSearch={() => {
                setKeyword(keywordInput.trim());
                setPage(1);
              }}
            />
            <Select
              allowClear
              aria-label="角色状态"
              placeholder="全部状态"
              style={{ width: 150 }}
              value={status}
              onChange={(value) => {
                setStatus(value as AuthzRoleStatus | undefined);
                setPage(1);
              }}
              options={[
                { label: "已启用", value: "enabled" },
                { label: "已停用", value: "disabled" },
              ]}
            />
          </Space>
          <PermissionGate user={currentUserQuery.data} permission={AUTHZ_MANAGEMENT_PERMISSIONS.create}>
            <Button type="primary" icon={<Plus size={16} />} onClick={() => setCreateVisible(true)}>
              新增角色
            </Button>
          </PermissionGate>
        </div>
      </Card>

      <Card>
        {rolesQuery.isError ? <Empty description="角色列表加载失败，请稍后重试" /> : null}
        <Table
          rowKey="id"
          loading={rolesQuery.isLoading}
          columns={columns}
          data={rolesQuery.data?.items ?? []}
          pagination={false}
          noDataElement={<Empty description="暂无角色" />}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Pagination
            showTotal
            showJumper
            sizeCanChange
            current={page}
            pageSize={pageSize}
            total={rolesQuery.data?.total ?? 0}
            onChange={setPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
          />
        </div>
      </Card>

      <Modal
        title="新增角色"
        visible={createVisible}
        confirmLoading={createMutation.isPending}
        onCancel={() => setCreateVisible(false)}
        onOk={submitCreate}
      >
        <Form layout="vertical">
          <Form.Item label="角色编码" required>
            <Input
              aria-label="角色编码"
              placeholder="例如 crm_operator"
              value={createPayload.code}
              onChange={(code) => setCreatePayload((current) => ({ ...current, code }))}
            />
          </Form.Item>
          <Form.Item label="角色名称" required>
            <Input
              aria-label="角色名称"
              value={createPayload.name}
              onChange={(name) => setCreatePayload((current) => ({ ...current, name }))}
            />
          </Form.Item>
          <Form.Item label="角色说明">
            <Input.TextArea
              aria-label="角色说明"
              value={createPayload.description}
              onChange={(description) => setCreatePayload((current) => ({ ...current, description }))}
            />
          </Form.Item>
          <Typography.Text type="secondary">角色编码创建后保持稳定，第一版仅支持启用或停用。</Typography.Text>
        </Form>
      </Modal>
    </Space>
  );
}
